import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatPaginator, MatSort, MatDialog } from '@angular/material';
import { TableGatesDataSource, GateTableData } from './table-gates-datasource';
import { SelectionModel } from '@angular/cdk/collections';
import { Observable, Subscription, merge } from 'rxjs'; 
import { map, mergeMap } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Entrance } from '../../models/entrance';
import { StoreSelectorService } from '../../services/storeselector.service';
import { EntranceService } from '../../services/entrance.service';
import { GateService } from '../../services/gate.service';
import { GateDialogComponent } from './gate-dialog/gate-dialog.component';
import { StoreSelection } from '../../models/store';
import { Gate } from '../../models/gate';
import { Screen } from '../../models/screen';
import { AuthService } from '../../services/auth.service';
import { forEach } from '@angular/router/src/utils/collection';


const DISPLAYED_COLUMNS_XSMALL: number = 2;
const DISPLAYED_COLUMNS_SMALL: number = 3;
const DISPLAYED_COLUMNS_MEDIUM: number = 4;

const PROGRESS_BAR_MODE_DETERMINATE: string = "determinate";
const PROGRESS_BAR_MODE_INDETERMINATE: string = "indeterminate";

const WARNING_NO_STORE: string = "No store selected.";
const WARNING_NO_GATES: string = "No gates found.";

@Component({
  selector: 'table-gates',
  host: {
    "[style.display]": "'block'"
  },
  templateUrl: './table-gates.component.html',
  styleUrls: ['./table-gates.component.scss']
})
export class TableGatesComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: TableGatesDataSource;
  entrances: Entrance[];
  gates: Gate[];
  // screens: Screen[];

  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = DISPLAYED_COLUMNS_XSMALL;
  progressBarMode: string;

  filterValue: string; // used to clear filter with X button

  private selectedStoreChangesSubscription: Subscription;

  // Table item selection model
  selection = new SelectionModel<GateTableData>(true, []);

  constructor(private _authService: AuthService,
              private dialog: MatDialog, 
              private breakpointObserver: BreakpointObserver,
              private _storeSelectorService: StoreSelectorService,
              private _entranceService: EntranceService,
              private _gateService: GateService,
              ) {}
  
  // Open dialog when a row is clicked
  openBrightnessDialog(selectedGate: GateTableData) {
    let dialogRef = this.dialog.open(GateDialogComponent, {
      data: {
        gate: selectedGate.gate,
        entrance: selectedGate.entrance,
        screens: selectedGate.screens
      },
      minWidth: "350px",
      width: "80vh",
      maxWidth: "700px",
      // height: "100%"
    });
    dialogRef.afterClosed().subscribe(result => {
      //safer to refresh all data after brightness has been changed, updates all resources
      if (result[1] == true) { //content changed flag
        this.onRefreshClick();
      }
      // let gateIndex = this.gates.findIndex(g => g.gateGuid == selectedGate.gate.gateGuid);
      // if (gateIndex != -1) {
      //   this.gates[gateIndex] = result[0];
      // }
    });
  }

  ngOnInit() {
    this.dataSource = new TableGatesDataSource(this.paginator, this.sort);

    // Change Columns on XSmall Breakpoint
    this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          // change displayed columns
          this.displayedColumns = DISPLAYED_COLUMNS_XSMALL;
        }
    });
    // Change Columns on Small Breakpoint
    this.breakpointObserver
      .observe([Breakpoints.Small])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          // change displayed columns
          this.displayedColumns = DISPLAYED_COLUMNS_SMALL;
        }
    });
    // Change Columns on Medium Breakpoint
    this.breakpointObserver
      .observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          // change displayed columns
          this.displayedColumns = DISPLAYED_COLUMNS_MEDIUM;
        }
    });

    /* Selected Store */
    // Get initial selected store
    let initSelectedStore: StoreSelection = this._storeSelectorService.getSelectedStore();
    this.onStoreChange(initSelectedStore);

    // Update Selected Store UI when selected store changes
    this.selectedStoreChangesSubscription = this._storeSelectorService.storeChanges.subscribe((newSelectedStore: StoreSelection) => {
      this.onStoreChange(newSelectedStore);
    });
  }

  selectedStore: StoreSelection;

  private onStoreChange(newSelectedStore: StoreSelection) {
    this.selectedStore = newSelectedStore;
    this.getGates();
  }

  ngOnDestroy() {
	  this.selectedStoreChangesSubscription.unsubscribe();
  }

  // Returns true if valid, false if invalid
  private checkStoreIsValid(): boolean {
    if (this.selectedStore === null) {
      return false;
    }
    return true;
  }

  isLoadingResults = true;
  hasError = false;
  hasWarning = false;
  errorMessage = "";
  warningMessage = "";

  requestNumber: number = 0;
  numCurrentGateRequests: number = 0;
  numCompleteGateRequests: number = 0;
  numErrorsFromGateRequest: number = 0;

  // Get gates and add them to datasource
  getGates() {
    if(!this.checkStoreIsValid()) {
      // Display warning that there is no store selected
      this.isLoadingResults = false;
      this.warningMessage = WARNING_NO_STORE;
      this.hasWarning = true;
      this.hasError = false;
      // No store, Show no data
      this.dataSource.data = [];
      return;
    }

    // this.clearTable();
    this.isLoadingResults = true;
    this.progressBarMode = PROGRESS_BAR_MODE_INDETERMINATE;
    this.hasError = false;
    this.hasWarning = false;

    var data = this._gateService.getStoreGates(this.selectedStore.storeGuid).pipe(
      map(response => this.gates = response),
      mergeMap(() => this._entranceService.getStoreEntrances(this.selectedStore.storeGuid)),
      map(response => this.entrances = response),
      // mergeMap(() => this._gateService.getStoreScreens(this.selectedStore.storeGuid)),
      // map(response => this.screens = response)
    );

    data.subscribe(() => { 
      this.clearTable();
      this.isLoadingResults = false;
      
      let tableData: GateTableData[] = [];
      if (this.gates != null) {
        this.gates.forEach(gate => {
          let gateData = new GateTableData();
          gateData.gate = gate;
          gateData.entrance = this.entrances.find(e => e.entranceGuid == gate.entranceGuid);
          // if (this.screens != null)
          //   gateData.screens = this.screens.filter(s => s.gateGuid == gate.gateGuid)
          
          tableData.push(gateData);
        });
      }

      this.dataSource.data = tableData;

    }, (error:any) => { this.clearTable(); this.showTableError(error) });
  }

  clearTable() {
    // Clear selection
    this.selection.clear();
    // On clear, Disable error message
    this.hasError = false;
    this.hasWarning = false;
    this.progressBarMode = PROGRESS_BAR_MODE_INDETERMINATE;
    const noGates = [];
    this.dataSource.data = noGates;
  }

  showTableError(err: any) {
    // On error, Show no data
    this.dataSource.data = [];
    // On error, Disable loading bar
    this.isLoadingResults = false;
    // On error, Set user facing error message to error from event.service
    this.errorMessage = err;
    // On error, Show loading message
    this.hasError = true;
    this.hasWarning = false;
  }

  onRefreshClick() {
    this.getGates();
  }

  logout(): void {
    this._authService.Logoff();
  }
}
